import { Divider, Stack } from '@mui/material';
import { useFeatureEnabled } from 'api/appConfig';
import EnabledFeatureCheck from 'components/EnabledFeatureCheck';
import { ROUTES } from 'constants/routes';
import { GuideType } from 'consts';
import { useIdPConfigContext } from 'contexts/IdPConfigContext';
import useIsAuditEnabled from 'hooks/useIsAuditEnabled';
import React, { useCallback, useEffect, useState } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { S4Feature } from 'types/api';
import AuditMenuSection from './AuditMenuSection';
import JITMenuSection from './JITMenuSection';
import SCIMMenuSection from './SCIMMenuSection';
import SSOMenuSection from './SSOMenuSection';
import XAAMenuSection from './XAAMenuSection';

const SECTIONS = {
	SSO_CONFIGURE: 'sso_configure',
	SCIM_CONFIGURE: 'scim_configure',
	XAA_CONFIGURE: 'xaa_configure',
	JIT_CONFIGURE: 'jit_configure',
	AUDIT: 'audit'
};

const ConfigurationsSidebar: React.FC = () => {
	const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
	const [openSectionByRoute, setOpenSectionByRoute] = useState<string | null>(
		null
	);
	const location = useLocation();

	const { guideType, isSubTenant } = useIdPConfigContext();
	const { enabled: ssoEnabled } = useFeatureEnabled(S4Feature.Sso);
	const { enabled: auditEnabled } = useIsAuditEnabled();

	useEffect(() => {
		if (matchPath(location.pathname, ROUTES.jit)) {
			setOpenSectionByRoute(SECTIONS.JIT_CONFIGURE);
		} else if (matchPath(ROUTES.audit, location.pathname)) {
			// Checked before the guide type, which still holds the last guide visited.
			setOpenSectionByRoute(SECTIONS.AUDIT);
		} else if (
			guideType === GuideType.SSO ||
			matchPath(location.pathname, ROUTES.selectIdP)
		) {
			setOpenSectionByRoute(SECTIONS.SSO_CONFIGURE);
		} else if (
			guideType === GuideType.SCIM ||
			matchPath(location.pathname, ROUTES.selectScim)
		) {
			setOpenSectionByRoute(SECTIONS.SCIM_CONFIGURE);
		} else if (
			guideType === GuideType.XAA ||
			matchPath(location.pathname, ROUTES.selectXaa)
		) {
			setOpenSectionByRoute(SECTIONS.XAA_CONFIGURE);
		}
	}, [location.pathname, guideType, setOpenSectionByRoute]);

	useEffect(() => {
		if (openSectionByRoute) {
			setOpenSections((openSectionsVal) => ({
				...openSectionsVal,
				[openSectionByRoute]: true
			}));
		}
	}, [openSectionByRoute]);

	const toggleOpenSection = useCallback(
		(section: string) => {
			setOpenSections({
				...openSections,
				[section]: !openSections[section]
			});
		},
		[openSections]
	);

	return (
		<Stack gap={2} minHeight="100%">
			{ssoEnabled && (
				<SSOMenuSection
					open={openSections[SECTIONS.SSO_CONFIGURE]}
					toggleOpen={() => toggleOpenSection(SECTIONS.SSO_CONFIGURE)}
				/>
			)}
			{!isSubTenant && (
				<EnabledFeatureCheck feature={S4Feature.Scim}>
					{ssoEnabled && <Divider sx={{ mx: 2 }} />}
					<SCIMMenuSection
						open={openSections[SECTIONS.SCIM_CONFIGURE]}
						toggleOpen={() => toggleOpenSection(SECTIONS.SCIM_CONFIGURE)}
					/>
				</EnabledFeatureCheck>
			)}
			{!isSubTenant && (
				<EnabledFeatureCheck feature={S4Feature.Xaa}>
					<Divider sx={{ mx: 2 }} />
					<XAAMenuSection
						open={openSections[SECTIONS.XAA_CONFIGURE]}
						toggleOpen={() => toggleOpenSection(SECTIONS.XAA_CONFIGURE)}
					/>
				</EnabledFeatureCheck>
			)}
			{!isSubTenant && (
				<EnabledFeatureCheck feature={S4Feature.JitGuide}>
					<Divider sx={{ mx: 2 }} />
					<JITMenuSection
						open={openSections[SECTIONS.JIT_CONFIGURE]}
						toggleOpen={
							// istanbul ignore next
							() => toggleOpenSection(SECTIONS.JIT_CONFIGURE)
						}
					/>
				</EnabledFeatureCheck>
			)}
			{auditEnabled && (
				<>
					<Divider sx={{ mx: 2 }} />
					<AuditMenuSection
						open={openSections[SECTIONS.AUDIT]}
						toggleOpen={() => toggleOpenSection(SECTIONS.AUDIT)}
					/>
				</>
			)}
		</Stack>
	);
};

export default ConfigurationsSidebar;
